/** @jsx isolateComponent */
import { useEffect } from 'react';

import { Document } from '@zapier/common-components';
import { isolateComponent, labelStyles } from '@zapier/common-utils';
import { Colors, GlobalStylesProvider } from '@zapier/design-system';
import { emit } from '@zapier/events-toolkit-browser';
import { useMediaProps } from '@zapier/responsive-utils';
import { Screen } from '@zapier/theme';

import Form from 'app/get-help/components/Form';
import { TrackingProvider } from 'app/get-help/contexts/TrackingContext';
import { FormProvider, SizeType } from 'app/get-help/contexts/FormContext';

import { PAGE_VIEW_EVENT } from 'app/get-help/constants';
import useScript from 'app/get-help/hooks/useScript';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

type AppProps = {
  query: {
    [key: string]: string;
  };
};

const Styles = labelStyles(__filename, {
  root: {
    backgroundColor: Colors.neutral100,
    flex: 1,
  },
});

const queryClient = new QueryClient();

const App = ({ query }: AppProps) => {
  const size: SizeType = useMediaProps({
    all: {
      size: 'small',
    },
    [Screen.queries.tablet]: { size: 'medium' },
    [Screen.queries.desktop]: { size: 'large' },
  }).size;

  useEffect(() => {
    // PageViewEvent to be run only once on page load
    emit(
      {
        system_name: 'support',
      },
      PAGE_VIEW_EVENT
    );
  }, []);

  // Load Intercom
  useScript('https://cdn.zapier.com/packages/intercom/v1/index.js');

  return (
    /*
     * As per https://async.zapier.com/p/65285/the-re-rebrand-and-engineering-clean-up-time:
     * Probably not the right time to remove GlobalStylesProvider if you're in the monolith.
     * We are not using isPlatform or isRebrand in the contact form app,
     * but still passing them in the provider just in case.
     */
    <GlobalStylesProvider isPlatform={true} isRebrand={true}>
      <Document title="Get Help">
        <section css={Styles.root}>
          <QueryClientProvider client={queryClient}>
            <FormProvider query={query} size={size}>
              {({ isLoading }) => {
                if (isLoading) {
                  // TODO: Spinner something
                  return null;
                }

                return (
                  <TrackingProvider>
                    <Form />
                  </TrackingProvider>
                );
              }}
            </FormProvider>
          </QueryClientProvider>
        </section>
      </Document>
    </GlobalStylesProvider>
  );
};

export default App;
